import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';

const HeroSection: React.FC = () => {
    const [scrollY, setScrollY] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleCTAClick = () => {
        document.getElementById('adhesion')?.scrollIntoView({
            behavior: 'smooth'
        });
    };

    return (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">

            {/* Background */}
            <div className="absolute inset-0 z-0">
                {/* Image de fond avec parallax */}
                <div
                    className="absolute bg-cover bg-center bg-no-repeat"
                    style={{
                        backgroundImage: `url('/images/herobg.jpg')`,
                        filter: 'brightness(0.25)',
                        width: '110%',
                        height: '110%',
                        top: '-5%',
                        left: '-5%',
                        transform: `translateY(${scrollY * 0.5}px)`
                    }}
                />

                {/* Overlay gradient */}
                <div
                    className="absolute inset-0 bg-gradient-to-br from-primary-blue/70 via-transparent to-primary-green/70"
                    style={{
                        transform: `translateY(${scrollY * 0.3}px)`
                    }}
                />

                {/* Overlay noir */}
                <div className="absolute inset-0 bg-black/40" />

                {/* Particules flottantes */}
                <div className="absolute inset-0">
                    {[...Array(30)].map((_, i) => (
                        <div
                            key={i}
                            className="absolute rounded-full animate-float-up"
                            style={{
                                width: Math.random() * 4 + 1 + 'px',
                                height: Math.random() * 4 + 1 + 'px',
                                left: Math.random() * 100 + '%',
                                bottom: '-20px',
                                backgroundColor: i % 3 === 0 ? '#fbbf24' : i % 3 === 1 ? '#16a34a' : '#1e40af',
                                opacity: Math.random() * 0.5 + 0.3,
                                animationDuration: `${Math.random() * 10 + 10}s`,
                                animationDelay: `${Math.random() * 10}s`,
                                filter: 'blur(0.5px)',
                                boxShadow: `0 0 ${Math.random() * 10 + 5}px currentColor`
                            }}
                        />
                    ))}
                </div>

                {/* Effet de vague en bas */}
                <div className="absolute bottom-0 left-0 right-0 h-32">
                    <svg className="absolute bottom-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 1440 100">
                        <path
                            fill="rgba(255, 255, 255, 0.05)"
                            d="M0,50 C480,100 960,0 1440,50 L1440,100 L0,100 Z"
                            className="animate-wave"
                        />
                        <path
                            fill="rgba(251, 191, 36, 0.03)"
                            d="M0,70 C480,20 960,120 1440,70 L1440,100 L0,100 Z"
                            className="animate-wave-reverse"
                        />
                    </svg>
                </div>
            </div>

            {/* Contenu principal */}
            <div className="relative z-10 container mx-auto px-4 text-center">
                <div className="min-h-screen flex flex-col justify-end pb-20">
                    <div className="max-w-4xl mx-auto">
                        {/* Logo UDB avec effet parallax et animation */}
                        <div
                            className="mb-4 animate-fade-in"
                            style={{
                                animationDelay: '0.1s',
                                transform: `translateY(${scrollY * -0.05}px)`
                            }}
                        >
                            <img
                                src="/logohero.png"
                                alt="Union Démocratique des Bâtisseurs"
                                className="mx-auto h-52 md:h-52 lg:h-60 w-auto drop-shadow-2xl hover:scale-105 transition-transform duration-300"
                                style={{
                                    filter: 'drop-shadow(0 0 20px rgba(251, 191, 36, 0.3))'
                                }}
                                onError={(e) => {
                                    console.warn('Logo hero non trouvé, utilisation du fallback');
                                    e.currentTarget.style.display = 'none';
                                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                    if (fallback) fallback.style.display = 'block';
                                }}
                            />

                            {/* Fallback si l'image ne charge pas */}
                            <div
                                className="hidden mx-auto w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 bg-gradient-to-br from-primary-blue to-primary-green rounded-full flex items-center justify-center drop-shadow-2xl"
                                style={{
                                    filter: 'drop-shadow(0 0 20px rgba(251, 191, 36, 0.3))'
                                }}
                            >
                                <span className="text-white font-bold text-2xl md:text-3xl lg:text-4xl">UDB</span>
                            </div>
                        </div>

                        {/* Titre principal avec effet parallax subtil */}
                        <h1
                            className="text-5xl md:text-6xl lg:text-7xl font-light text-white mb-6 animate-fade-in leading-tight"
                            style={{
                                animationDelay: '0.2s',
                                transform: `translateY(${scrollY * -0.1}px)`
                            }}
                        >
                            Bâtir le Gabon
                            <span className="block text-primary-yellow font-light">de demain</span>
                        </h1>

                        {/* Sous-titre */}
                        <p
                            className="text-xl md:text-2xl text-white/90 mb-10 animate-fade-in max-w-2xl mx-auto font-light"
                            style={{
                                animationDelay: '0.3s',
                                transform: `translateY(${scrollY * -0.15}px)`
                            }}
                        >
                            Ensemble bâtissons le Gabon nouveau digne d'envie
                        </p>

                        {/* Call to Action */}
                        <div
                            className="animate-fade-in"
                            style={{
                                animationDelay: '0.4s',
                                transform: `translateY(${scrollY * -0.2}px)`
                            }}
                        >
                            <button
                                onClick={handleCTAClick}
                                className="bg-white text-primary-blue px-6 py-3 font-normal hover:bg-primary-yellow hover:text-gray-900 transition-all uppercase tracking-wide transform hover:scale-105"
                            >
                                Rejoignez le mouvement
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Indicateur de scroll */}
            <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-20">
                <button
                    onClick={() => document.getElementById('president-message')?.scrollIntoView({ behavior: 'smooth' })}
                    className="text-white/60 hover:text-white transition-colors"
                    aria-label="Faire défiler vers le bas"
                >
                    <ChevronDown className="w-6 h-6 animate-bounce" />
                </button>
            </div>

            <style>{`
                @keyframes float-up {
                    0% {
                        transform: translateY(0) rotate(0deg);
                        opacity: 0;
                    }
                    10% {
                        opacity: 1;
                    }
                    90% {
                        opacity: 1;
                    }
                    100% {
                        transform: translateY(-100vh) rotate(360deg);
                        opacity: 0;
                    }
                }

                @keyframes fade-in {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes wave {
                    0%, 100% { transform: translateX(0%); }
                    50% { transform: translateX(-25%); }
                }

                @keyframes wave-reverse {
                    0%, 100% { transform: translateX(0%); }
                    50% { transform: translateX(25%); }
                }

                .animate-float-up {
                    animation: float-up linear infinite;
                }

                .animate-fade-in {
                    animation: fade-in 0.8s ease-out forwards;
                    opacity: 0;
                }

                .animate-wave {
                    animation: wave 20s ease-in-out infinite;
                }

                .animate-wave-reverse {
                    animation: wave-reverse 15s ease-in-out infinite;
                }
            `}</style>
        </section>
    );
};

export default HeroSection;
